// Custom color utilities to replace culori dependency

export interface RgbColor {
    mode: 'rgb';
    r: number;
    g: number;
    b: number;
    alpha?: number;
}

export interface LchColor {
    mode: 'lch';
    l: number;
    c: number;
    h: number;
    alpha?: number;
}

export type Color = RgbColor | LchColor;

export interface Lch {
    mode: 'lch';
    l: number;
    c: number;
    h: number;
}

export interface Rgb {
    mode: 'rgb';
    r: number;
    g: number;
    b: number;
}

// Convert RGB to LCH color space
export const rgb2lch = (color: Color | RgbColor): LchColor => {
    if (color.mode === 'lch') {
        return color as LchColor;
    }

    const rgbColor = color as RgbColor;
    const { r, g, b } = rgbColor;

    // Convert RGB to XYZ
    const toLinear = (c: number): number => {
        if (c <= 0.04045) {
            return c / 12.92;
        } else {
            return Math.pow((c + 0.055) / 1.055, 2.4);
        }
    };

    const rLinear = toLinear(r);
    const gLinear = toLinear(g);
    const bLinear = toLinear(b);

    // Observer = 2°, Illuminant = D65
    let x = rLinear * 0.4124564 + gLinear * 0.3575761 + bLinear * 0.1804375;
    let y = rLinear * 0.2126729 + gLinear * 0.7151522 + bLinear * 0.0721750;
    let z = rLinear * 0.0193339 + gLinear * 0.1191920 + bLinear * 0.9503041;

    // Normalize for D65 illuminant
    x = x / 0.95047;
    y = y / 1.00000;
    z = z / 1.08883;

    // Convert XYZ to LAB
    const xyz2lab = (t: number): number => {
        if (t > 0.008856) {
            return Math.pow(t, 1/3);
        } else {
            return (7.787 * t) + (16/116);
        }
    };

    const fx = xyz2lab(x);
    const fy = xyz2lab(y);
    const fz = xyz2lab(z);

    const l = (116 * fy) - 16;
    const a = 500 * (fx - fy);
    const b_lab = 200 * (fy - fz);

    // Convert LAB to LCH
    const c = Math.sqrt(a * a + b_lab * b_lab);
    let h = Math.atan2(b_lab, a) * (180 / Math.PI);
    if (h < 0) h += 360;

    return {
        mode: 'lch',
        l: Math.max(0, Math.min(100, l)),
        c: Math.max(0, c),
        h: isNaN(h) ? 0 : h,
        alpha: rgbColor.alpha
    };
};

// Convert LCH to RGB color space
export const lch2rgb = (color: Color | LchColor): RgbColor => {
    if (color.mode === 'rgb') {
        return color as RgbColor;
    }

    const lchColor = color as LchColor;
    const { l, c, h, alpha } = lchColor;

    // Convert LCH to LAB
    const hRad = (h || 0) * (Math.PI / 180);
    const a = c * Math.cos(hRad);
    const b_lab = c * Math.sin(hRad);

    // Convert LAB to XYZ
    const fy = (l + 16) / 116;
    const fx = a / 500 + fy;
    const fz = fy - b_lab / 200;

    const lab2xyz = (t: number): number => {
        const t3 = t * t * t;
        if (t3 > 0.008856) {
            return t3;
        } else {
            return (t - 16/116) / 7.787;
        }
    };

    let x = lab2xyz(fx) * 0.95047;
    let y = lab2xyz(fy) * 1.00000;
    let z = lab2xyz(fz) * 1.08883;

    // Convert XYZ to RGB
    let r = x * 3.2404542 + y * -1.5371385 + z * -0.4985314;
    let g = x * -0.9692660 + y * 1.8760108 + z * 0.0415560;
    let b = x * 0.0556434 + y * -0.2040259 + z * 1.0572252;

    // Apply gamma correction
    const fromLinear = (c: number): number => {
        if (c <= 0.0031308) {
            return 12.92 * c;
        } else {
            return 1.055 * Math.pow(c, 1/2.4) - 0.055;
        }
    };

    r = Math.max(0, Math.min(1, fromLinear(r)));
    g = Math.max(0, Math.min(1, fromLinear(g)));
    b = Math.max(0, Math.min(1, fromLinear(b)));

    return {
        mode: 'rgb',
        r,
        g,
        b,
        alpha
    };
};

// Get RGB values from any color
export const rgb = (color: Color): RgbColor => {
    return lch2rgb(color);
};

// Get LCH values from any color  
export const lch = (color: Color): LchColor => {
    return rgb2lch(color);
};

// Calculate Euclidean distance between two colors in LCH space
export const differenceEuclidean = (colorA: Color, colorB: Color): number => {
    const lchA = rgb2lch(colorA);
    const lchB = rgb2lch(colorB);

    const deltaL = lchA.l - lchB.l;
    const deltaC = lchA.c - lchB.c;
    
    // Handle hue difference (circular)
    let deltaH = Math.abs(lchA.h - lchB.h);
    if (deltaH > 180) {
        deltaH = 360 - deltaH;
    }

    return Math.sqrt(deltaL * deltaL + deltaC * deltaC + deltaH * deltaH);
};

// Create a distance function for LCH color space
export const differenceEuclideanLch = () => differenceEuclidean;

// Find the nearest colors from a palette
export const nearest = (palette: Color[], distanceFunction: (a: Color, b: Color) => number) => {
    return (targetColor: Color, count: number = 1): Color[] => {
        const distances = palette.map(paletteColor => ({
            color: paletteColor,
            distance: distanceFunction(targetColor, paletteColor)
        }));

        distances.sort((a, b) => a.distance - b.distance);

        return distances.slice(0, count).map(item => item.color);
    };
};